/**
 * The event catalogue (spec C1–C3). One entry per event; names are typed once
 * and `REGISTRY` is derived from this list.
 *
 * Adding an event: one `defineEvent`/`defineOutcome`/`defineWorkflow` line in
 * the right area below, then `pnpm analytics:docs`.
 */

import { ERROR_KINDS, FAIL_PROPS, defineEvent, defineOutcome, defineWorkflow, p, type EventDef, type PropSpec } from './props';

const V3 = ['crm_v3'] as const;
const API = ['api'] as const;
const STUDIO_V3 = ['studio_v3'] as const;
const STUDIOS = ['studio_v3', 'studio_v2'] as const;
const LANDING = ['landing'] as const;

const server = { source: 'server', apps: API } as const;
const v3Client = { source: 'client', apps: V3 } as const;
const studioClient = { source: 'client', apps: STUDIO_V3 } as const;
const v2Client = { source: 'client', apps: ['crm_v2'] as const } as const;

// ---------------------------------------------------------------------------
// C1. Generic seams
// ---------------------------------------------------------------------------

const MUTATION_PROPS = {
  procedure: p.string(128),
  domain: p.nullable(p.string(64)),
  tier: p.nullable(p.string(32)),
  duration_ms: p.number(),
  request_id: p.nullable(p.string(64)),
} as const;

const generic = [
  defineEvent('api:mutation_complete', {
    description: 'A CRM tRPC mutation called from V3 or Studio succeeded (server middleware, generic seam).',
    source: 'server',
    apps: API,
    props: MUTATION_PROPS,
  }),
  defineEvent('api:mutation_fail', {
    description: 'A CRM tRPC mutation called from V3 or Studio failed (server middleware, generic seam).',
    source: 'server',
    apps: API,
    props: {
      ...MUTATION_PROPS,
      ...FAIL_PROPS,
      iam_reason: p.nullable(p.string(64)),
      invalid_fields: p.array(p.string(128)),
    },
  }),
  defineEvent('api:query_deny', {
    description: 'A CRM tRPC query was refused with FORBIDDEN; deduped per user and procedure for 10 minutes.',
    source: 'server',
    apps: API,
    props: { procedure: p.string(128), iam_reason: p.nullable(p.string(64)) },
  }),
  defineEvent('api:request_fail', {
    description: 'A V3 tRPC request never got a usable answer (network, gateway, timeout, parse); deduped per session.',
    ...v3Client,
    props: {
      procedure: p.string(128),
      http_status: p.nullable(p.number()),
      error_kind: p.enum(['network', 'gateway', 'timeout', 'parse'] as const),
      error_code: p.nullable(p.string(64)),
      request_id: p.nullable(p.string(64)),
      is_online: p.boolean(),
    },
  }),
  defineEvent('ui:error_toast_show', {
    description: 'An error toast was shown in V3. Never carries the toast text, only codes and ids.',
    ...v3Client,
    props: {
      route_id: p.nullable(p.string(256)),
      toast_id: p.nullable(p.string(64)),
      has_description: p.boolean(),
      procedure: p.nullable(p.string(128)),
      error_code: p.nullable(p.string(64)),
      request_id: p.nullable(p.string(64)),
    },
  }),
  defineEvent('ui:permission_deny_view', {
    description: 'A V3 detail screen rendered its FORBIDDEN state instead of the data.',
    ...v3Client,
    props: { route_id: p.nullable(p.string(256)), iam_reason: p.nullable(p.string(64)) },
  }),
  defineEvent('ui:empty_state_view', {
    description: 'A V3 empty state was shown; once per session per empty state id.',
    ...v3Client,
    props: { route_id: p.nullable(p.string(256)), empty_state_id: p.string(64) },
  }),
  defineEvent('ui:drawer_open', {
    description: 'A V3 drawer opened; drawer_id is its analyticsId or the route id.',
    ...v3Client,
    props: { drawer_id: p.string(128) },
  }),
  defineEvent('ui:drawer_close', {
    description: 'A V3 drawer closed after the user committed its change (outcome commit only).',
    ...v3Client,
    props: { drawer_id: p.string(128), outcome: p.enum(['commit'] as const), duration_ms: p.number() },
  }),
  defineEvent('ui:dialog_open', {
    description: 'A V3 dialog opened; dialog_id is its analyticsId.',
    ...v3Client,
    props: { dialog_id: p.string(128) },
  }),
  defineEvent('nav:org_switch', {
    description: 'The user switched organisation from the V3 shell switcher.',
    ...v3Client,
    props: {},
  }),
  defineEvent('nav:store_switch', {
    description: 'The user switched store from the V3 shell switcher.',
    ...v3Client,
    props: {},
  }),
] as const;

// ---------------------------------------------------------------------------
// C2. Named server events (success + `_fail`, emitted by the tRPC middleware)
// ---------------------------------------------------------------------------

const serverPair = { success: server, fail: server } as const;

const named = [
  ...defineOutcome('onboarding', 'store_create', {
    description: 'A store was created (v3.stores.create, stores.create).',
    props: {},
    ...serverPair,
  }),
  ...defineWorkflow('processors', 'processor_connect', {
    description: 'A payment processor was connected (v3.processors.save, processors.create).',
    props: { processor_type: p.nullable(p.string(64)) },
    start: {
      ...v3Client,
      props: { method: p.enum(['credentials', 'oauth'] as const) },
      description: 'The user pressed Connect on a processor credential form or the Stripe Connect button.',
    },
    ...serverPair,
  }),
  ...defineOutcome('processors', 'stripe_connect_start', {
    description: 'A Stripe Connect onboarding link was created (v3.processors.initiateStripeConnect).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('payment_flows', 'payment_flow_create', {
    description: 'A payment flow was created (paymentFlows.create).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('canvas', 'funnel_create', {
    description: 'A funnel was created on the canvas (v3.canvas.createFunnel).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('canvas', 'funnel_publish', {
    description: 'A checkout funnel was published (v3.onboarding.deployCheckout, funnels.promoteToProduction).',
    props: { via: p.enum(['onboarding', 'promote'] as const) },
    ...serverPair,
  }),
  ...defineOutcome('canvas', 'test_run_create', {
    description: 'A canvas test run was created (v3.canvas.createTestRun).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('canvas', 'payment_mode_update', {
    description: 'The canvas payment mode was switched between test and live (v3.canvas.setPaymentMode).',
    props: { mode: p.enum(['test', 'live'] as const) },
    ...serverPair,
  }),
  ...defineOutcome('catalog', 'product_create', {
    description: 'A catalog product was created (products.create).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('orders', 'order_refund', {
    description: 'An order or a payment was refunded (orders.refund, payments.refund).',
    props: { entity: p.enum(['order', 'payment'] as const) },
    ...serverPair,
  }),
  ...defineOutcome('team', 'member_invite', {
    description: 'A team member was invited or added (iamOrgs.membersInviteOwn, iamOrgs.membersAdd).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('settings', 'api_key_create', {
    description: 'An API key was created (apiKeys.create).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('experiments', 'experiment_start', {
    description: 'An experiment was started (v3.experiments.start).',
    props: {},
    ...serverPair,
  }),
  ...defineOutcome('data', 'csv_export', {
    description: 'A CSV export was produced (v3.orders/payments/customers/subscriptions.exportCsv).',
    props: { entity: p.enum(['order', 'payment', 'customer', 'subscription'] as const) },
    ...serverPair,
  }),
  // Sign-up: explicit server emit at the three self-serve creation sites, not
  // gated by the V3/Studio gate. Its failure is seen by the V3 client only.
  ...defineOutcome('onboarding', 'signup_complete', {
    description: 'The V3 self-serve sign-up created the account and the first store, and opens its canvas.',
    props: {},
    success: {
      ...v3Client,
      props: { platform: p.enum(['shopify', 'woocommerce', 'custom', 'tagada'] as const), track: p.enum(['ecom', 'sdk', 'pages'] as const) },
    },
    fail: {
      ...v3Client,
      props: { step_id: p.enum(['register', 'activate', 'complete'] as const) },
      description: 'The V3 self-serve sign-up could not finish; step_id is the phase that threw. The answers are kept for a retry.',
    },
  }),
  ...defineOutcome('onboarding', 'account_create', {
    description:
      'A self-serve sign-up inserted a new accounts row (completeHubSignup, account.provisionAccount, studio bootstrap).',
    props: {},
    success: {
      ...server,
      props: {
        signup_app: p.enum(['crm_v2', 'crm_v3', 'studio_v2', 'studio_v3'] as const),
        signup_method: p.enum(['email', 'google', 'studio', 'unknown'] as const),
        partner_slug: p.nullable(p.string(64)),
        has_acquisition: p.boolean(),
        utm_source: p.nullable(p.string(128)),
        utm_medium: p.nullable(p.string(128)),
        utm_campaign: p.nullable(p.string(128)),
        account_id: p.string(64),
        is_additional_account: p.boolean(),
      },
    },
    fail: {
      ...v3Client,
      props: { step_id: p.enum(['register', 'activate'] as const) },
      description: 'Account setup failed in the V3 wizard; step_id is the phase that threw.',
    },
  }),
] as const;

// ---------------------------------------------------------------------------
// C3. Client events by area
// ---------------------------------------------------------------------------

const landing = [
  defineEvent('landing:cta_click', {
    description: 'A call to action was clicked on www.tagada.io; cta_text is our own copy, cut to 40 chars.',
    source: 'client',
    apps: LANDING,
    props: {
      cta_kind: p.enum([
        'signup',
        'studio_create',
        'payfac_apply',
        'book_call',
        'contact_whatsapp',
        'contact_telegram',
        'docs',
        'referral',
        'ghl_form',
        'outbound',
      ] as const),
      section: p.nullable(p.string(64)),
      cta_text: p.string(40),
      destination_host: p.nullable(p.string(253)),
      page_type: p.string(64),
    },
  }),
  defineEvent('landing:form_submit', {
    description: 'A landing form was submitted successfully (careers application, RX waitlist).',
    source: 'client',
    apps: LANDING,
    props: { form_id: p.enum(['careers_apply', 'rx_waitlist'] as const) },
  }),
  defineEvent('landing:form_submit_fail', {
    description: 'A landing form submission failed; error_code is a code, never the message.',
    source: 'client',
    apps: LANDING,
    props: { form_id: p.enum(['careers_apply', 'rx_waitlist'] as const), ...FAIL_PROPS },
  }),
] as const;

const SIGN_UP_METHOD = p.enum(['email', 'google'] as const);

const auth = [
  defineEvent('auth:sign_up_start', {
    description: 'The user submitted the V3 sign-up form or pressed the Google button.',
    ...v3Client,
    props: { method: SIGN_UP_METHOD },
  }),
  defineEvent('auth:sign_up_fail', {
    description: 'V3 sign-up failed; error_code is the Clerk error code.',
    ...v3Client,
    props: { method: SIGN_UP_METHOD, ...FAIL_PROPS },
  }),
  defineEvent('auth:email_verify_complete', {
    description: 'The user verified their email address with the code sent during V3 sign-up.',
    ...v3Client,
    props: {},
  }),
  defineEvent('auth:email_verify_fail', {
    description: 'Email verification failed during V3 sign-up; error_code is the Clerk error code.',
    ...v3Client,
    props: { ...FAIL_PROPS },
  }),
  defineEvent('auth:sign_in_complete', {
    description: 'The user signed in to V3.',
    ...v3Client,
    props: { method: p.enum(['email', 'google', 'sso'] as const) },
  }),
  defineEvent('auth:sign_in_fail', {
    description: 'V3 sign-in failed; error_code is the Clerk error code.',
    ...v3Client,
    props: { method: p.enum(['email', 'google', 'sso'] as const), ...FAIL_PROPS },
  }),
] as const;

const V3_BANNER_STATE = p.enum(['open', 'join', 'waiting'] as const);

const WIZARD_STEP = {
  wizard: p.enum(['account_setup'] as const),
  step_id: p.string(64),
  step_index: p.number(),
} as const;

const onboarding = [
  defineEvent('onboarding:wizard_step_view', {
    description: 'The user reached a step of the V3 account setup wizard.',
    ...v3Client,
    props: WIZARD_STEP,
  }),
  defineEvent('onboarding:wizard_step_complete', {
    description: 'The user completed a step of the V3 account setup wizard.',
    ...v3Client,
    props: WIZARD_STEP,
  }),
  defineEvent('onboarding:signup_step_view', {
    description: 'The user reached a step of the V3 self-serve sign-up.',
    ...v3Client,
    props: { step_id: p.enum(['welcome', 'url', 'scan', 'path', 'setup', 'agreement', 'verify', 'finish'] as const) },
  }),
  defineEvent('onboarding:first_name_submit', {
    description: 'The user gave a first name on the first V3 sign-up screen. Never carries the name.',
    ...v3Client,
    props: {},
  }),
  defineEvent('onboarding:email_submit', {
    description: 'The user gave an email the V3 sign-up accepted (Clerk sign-up created, code not sent yet).',
    ...v3Client,
    props: {},
  }),
  defineEvent('onboarding:store_url_submit', {
    description: 'The user submitted a store URL to scan during the V3 sign-up. Never carries the URL.',
    ...v3Client,
    props: {},
  }),
  defineEvent('onboarding:no_site_select', {
    description: 'The user said they have no site yet during the V3 sign-up.',
    ...v3Client,
    props: {},
  }),
  defineEvent('onboarding:store_scan_complete', {
    description: 'v3.signup.scanStore answered during the V3 sign-up: what it found and how long it took.',
    ...v3Client,
    props: {
      status: p.enum(['found', 'blocked', 'unreachable', 'unknown'] as const),
      platform: p.nullable(p.enum(['shopify', 'woocommerce', 'custom'] as const)),
      duration_ms: p.number(),
      product_count: p.nullable(p.number()),
      app_count: p.number(),
      pixel_count: p.number(),
      has_subscriptions: p.boolean(),
    },
  }),
  defineEvent('onboarding:store_scan_fail', {
    description: 'v3.signup.scanStore failed during the V3 sign-up; the flow asks for the platform instead.',
    ...v3Client,
    props: { ...FAIL_PROPS },
  }),
  defineEvent('onboarding:platform_select', {
    description: 'The user named their platform after a scan that could not read the store.',
    ...v3Client,
    props: { platform: p.enum(['shopify', 'woocommerce', 'custom'] as const) },
  }),
  defineEvent('onboarding:build_path_select', {
    description: 'The user chose how to build (pages on the platform, or the SDK) during the V3 sign-up.',
    ...v3Client,
    props: { build_path: p.enum(['pages', 'sdk'] as const) },
  }),
  defineEvent('onboarding:setup_question_submit', {
    description: 'The user answered one setup question of the V3 sign-up and moved on.',
    ...v3Client,
    props: { question_id: p.enum(['brand', 'models', 'shipping', 'volume', 'payments'] as const), step_index: p.number() },
  }),
  defineEvent('onboarding:demo_scene_view', {
    description: 'A demo scene of the V3 sign-up canvas started playing.',
    ...v3Client,
    props: { scene_id: p.enum(['checkout', 'upsells', 'studio', 'ai', 'payments', 'sdk', 'growth', 'pages'] as const) },
  }),
  defineEvent('onboarding:agreement_accept', {
    description: 'The user signed the merchant agreement during the V3 sign-up.',
    ...v3Client,
    props: {},
  }),
  defineEvent('access:v3_gate_view', {
    description: 'A signed-in user was refused CRM V3 by RequireV3Access and saw the gate screen.',
    ...v3Client,
    props: {},
  }),
  defineEvent('access:v3_banner_view', {
    description: 'The CRM V3 banner of the V2 app was shown (open CRM V3, join the waitlist, or waitlist status).',
    ...v2Client,
    props: { state: V3_BANNER_STATE },
  }),
  defineEvent('access:v3_banner_click', {
    description: 'The call to action of the CRM V3 banner of the V2 app was clicked.',
    ...v2Client,
    props: { state: V3_BANNER_STATE },
  }),
  defineEvent('access:v3_banner_close', {
    description: 'The CRM V3 banner of the V2 app was closed; it stays hidden for 7 days on that organization.',
    ...v2Client,
    props: { state: V3_BANNER_STATE },
  }),
] as const;

const agent = [
  defineEvent('agent:panel_open', {
    description: 'The V3 agent panel was opened; entry is the entry point id.',
    ...v3Client,
    props: { entry: p.string(64) },
  }),
  defineEvent('agent:message_send', {
    description: 'The user sent a message to the V3 agent. Never carries the message text.',
    ...v3Client,
    props: { agent_mode: p.string(32), has_attachment: p.boolean(), turn_index: p.number() },
  }),
  defineEvent('agent:turn_complete', {
    description: 'The V3 agent finished answering one turn; trace_id joins the $ai_trace.',
    ...v3Client,
    props: { trace_id: p.nullable(p.string(64)) },
  }),
  defineEvent('agent:turn_fail', {
    description: 'A V3 agent turn failed; error_code is the chat-errors kind in snake_case (rate_limited, busy).',
    ...v3Client,
    props: { trace_id: p.nullable(p.string(64)), ...FAIL_PROPS },
  }),
] as const;

const COMMAND_SOURCE = p.enum(['user', 'studio_ai', 'host_assistant', 'mcp'] as const);

const studio = [
  // Host side (crm_v3)
  defineEvent('studio:frame_load', {
    description:
      'The Studio editor iframe loaded and connected inside V3. duration_ms: mount to editor shown; ready_ms: mount to Studio ready; token_ms: mount to the editing session minted (the iframe boots meanwhile, 0 when it was minted first); outcome: rendered, no_signal (Studio without the render signal, counted at ready) or render_timeout (shown after the 20 s wait).',
    ...v3Client,
    props: {
      duration_ms: p.number(),
      ready_ms: p.nullable(p.number()),
      token_ms: p.nullable(p.number()),
      outcome: p.enum(['rendered', 'no_signal', 'render_timeout'] as const),
    },
  }),
  defineEvent('studio:frame_load_fail', {
    description: 'The Studio editor iframe failed to load or to connect inside V3.',
    ...v3Client,
    props: {
      reason: p.enum(['load_failed', 'connect_timeout'] as const),
      error_kind: p.nullable(p.enum(ERROR_KINDS)),
      error_code: p.nullable(p.string(64)),
      request_id: p.nullable(p.string(64)),
    },
  }),
  defineEvent('studio:media_upload_fail', {
    description: 'A media upload requested by the Studio iframe failed on the V3 host.',
    ...v3Client,
    props: {
      reason: p.string(64),
      error_kind: p.nullable(p.enum(ERROR_KINDS)),
      error_code: p.nullable(p.string(64)),
      request_id: p.nullable(p.string(64)),
    },
  }),
  // Editor (studio_v3, plus the minimal studio_v2 set)
  defineEvent('studio:editor_open', {
    description: 'The Studio editor opened, embedded in V3 or standalone; entry says how the page started.',
    source: 'client',
    apps: STUDIOS,
    props: {
      mode: p.enum(['embedded', 'standalone'] as const),
      entry: p.nullable(p.enum(['blank', 'template', 'ai_studio', 'ai_mcp', 'existing'] as const)),
    },
  }),
  defineEvent('studio:start_choice_select', {
    description: 'The user picked a starting point in the Studio start screen.',
    ...studioClient,
    props: { choice: p.string(64) },
  }),
  defineEvent('studio:page_add', {
    description: 'A page was added in the Studio editor.',
    ...studioClient,
    props: { method: p.enum(['template', 'block', 'blank'] as const) },
  }),
  defineEvent('studio:command_fail', {
    description: 'An editor command failed; at most one per command type per session.',
    ...studioClient,
    props: {
      command_type: p.string(64),
      source: COMMAND_SOURCE,
      error_kind: p.nullable(p.enum(ERROR_KINDS)),
      error_code: p.nullable(p.string(64)),
      request_id: p.nullable(p.string(64)),
    },
  }),
  defineEvent('studio:page_save', {
    description: 'A Studio page was saved; carries the command mix since the previous save.',
    ...studioClient,
    props: {
      commands_by_type: p.record(p.number()),
      commands_by_source: p.record(p.number()),
      undo_count: p.number(),
      duration_ms: p.number(),
    },
  }),
  defineEvent('studio:page_save_fail', {
    description: 'Saving a Studio page failed.',
    ...studioClient,
    props: { ...FAIL_PROPS },
  }),
  defineEvent('studio:page_publish', {
    description: 'A Studio page was published from the publish modal.',
    ...studioClient,
    props: { is_preview_stale: p.boolean() },
  }),
  defineEvent('studio:page_publish_fail', {
    description: 'Publishing a Studio page failed.',
    ...studioClient,
    props: { ...FAIL_PROPS },
  }),
  ...defineWorkflow('studio', 'page_generate', {
    description: 'A page was generated by Studio AI.',
    props: {},
    start: studioClient,
    success: studioClient,
    fail: {
      ...studioClient,
      props: { reason: p.enum(['out_of_credits', 'stream_error', 'cancel', 'server'] as const) },
    },
  }),
  defineEvent('studio:generated_page_accept', {
    description: 'The user kept a page generated by Studio AI.',
    ...studioClient,
    props: {},
  }),
  defineEvent('studio:generated_page_reject', {
    description: 'The user discarded a page generated by Studio AI.',
    ...studioClient,
    props: {},
  }),
  defineEvent('studio:ai_turn_complete', {
    description: 'A Studio AI assistant turn ended; ended says how.',
    ...studioClient,
    props: { ended: p.enum(['text', 'cap', 'silent', 'aborted'] as const) },
  }),
  defineEvent('studio:ai_turn_fail', {
    description: 'A Studio AI assistant turn failed.',
    ...studioClient,
    props: { ...FAIL_PROPS },
  }),
  defineEvent('studio:signup_gate_view', {
    description: 'An anonymous Studio visitor hit the sign-up gate.',
    source: 'client',
    apps: STUDIOS,
    props: {},
  }),
] as const;

type AnyDef = EventDef<string, Record<string, PropSpec>>;

/** Every registered event, in catalogue order (tests and docs iterate this). */
export const EVENT_DEFS = [
  ...generic,
  ...named,
  ...landing,
  ...auth,
  ...onboarding,
  ...agent,
  ...studio,
] as const satisfies readonly AnyDef[];

type Defs = (typeof EVENT_DEFS)[number];

export type RegistryEventName = Defs['name'];
export type RegistryEventDef<E extends RegistryEventName> = Extract<Defs, { name: E }>;

function byName<const T extends readonly AnyDef[]>(defs: T): { readonly [D in T[number] as D['name']]: D } {
  const out: Record<string, AnyDef> = {};
  for (const def of defs) out[def.name] = def;
  return out as { readonly [D in T[number] as D['name']]: D };
}

export const REGISTRY = byName(EVENT_DEFS);
